"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Api = void 0;
const axios_1 = __importDefault(require("axios"));
const qs_1 = __importDefault(require("qs"));
const moment_1 = __importDefault(require("moment"));
const utils_1 = require("./utils");
const epicOSWUtils_1 = require("./epicOSWUtils");
const Filters_1 = require("./constants/Filters");
const Facets_1 = require("./constants/Facets");
const search_sdk_1 = require("@kyruus/search-sdk");
const Time_1 = require("./constants/Time");
const CONSENT = 'Y';
class Api {
    constructor({ customerCode, productName, apiBaseURL }) {
        this.customerCode = customerCode;
        this.apiBaseURL = apiBaseURL || '';
        const searchClientConfig = {
            customerCode,
            baseURL: this.apiBaseURL + '/api/searchservice-v9'
        };
        if (productName) {
            searchClientConfig.productName = productName;
        }
        this.searchClient = new search_sdk_1.Search(searchClientConfig);
        /* (possible) TODO - Define an instance property to hold
         an axios instance (generated via `axios.create`)
         for this class to use.
         
         This will be a fairly heavy lift as it will require
         unit tests be written for the Api class to confirm
         the correct endpoints are being hit, while mocking
         out the Api class in the state machine unit tests.
    
         Current state is we test the calls being made in the
         state machine unit tests.
        */
        axios_1.default.defaults.paramsSerializer = (params) => qs_1.default.stringify(params, { arrayFormat: 'brackets' });
    }
    /**
     * Returns a range object to be used by the api
     * @param {string} start - date string
     * @param {number} rangeDays
     * @returns {{ period_start: string, period_end: string}}
     */
    static buildRangeFromDate(start, rangeDays) {
        return {
            period_start: (0, utils_1.formatApiDate)(start),
            period_end: (0, utils_1.formatApiDate)((0, moment_1.default)(start).add(rangeDays, 'days').endOf('day'))
        };
    }
    /**
     * Returns 'AM' or 'PM' based on time of dateTime passed in
     * @param {String|Moment} dateTime - anything parseable by momentJs
     * @returns {String}
     */
    static appointmentTimeOfDay(dateTime) {
        // NOTE: using new Date here is faster but breaks in IE11 due to start_datetime formatting
        const hours = (0, moment_1.default)(dateTime).hours();
        return hours < 12 ? 'AM' : 'PM';
    }
    /**
     * Returns day of week based on dateTime passed in. Sunday would be "0"
     * @param {String|Moment} dateTime - anything parseable by momentJs
     * @returns {Number}
     */
    static appointmentDayOfWeek(dateTime) {
        return (0, moment_1.default)(dateTime).day();
    }
    /**
     * Enhances an appointment with useful properties
     * @param {Object} appointment
     * @returns {Object} decorated appointment
     */
    static decorateAppointment(appointment) {
        return Object.assign(Object.assign({}, appointment), { time_of_day: Api.appointmentTimeOfDay(appointment.start_datetime), day_of_week: Api.appointmentDayOfWeek(appointment.start_datetime), end_datetime: (0, utils_1.formatApiDate)((0, moment_1.default)(appointment.start_datetime).add(appointment.duration_minutes, 'minutes')) });
    }
    /**
     * @param {Object} params
     * @param {Object} params.provider
     * @param {Array} params.location_ids
     * @param {String|Moment} params.period_start anything parseable by MomentJs
     * @param {String} params.purpose
     * @param {String} params.patient_relationship
     * @param {Array} params.visibility
     * @returns {Object} api response
     */
    getNext({ location_ids, provider, period_start, purpose, patient_relationship, visibility }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { customerCode } = this;
            const res = yield axios_1.default.get(this.apiBaseURL +
                `/api/availability/${encodeURIComponent(customerCode)}/availability/next`, {
                params: {
                    location_codes: location_ids,
                    provider_code: provider.id,
                    period_start: (0, utils_1.formatApiDate)(period_start),
                    purpose: purpose,
                    patient_relationship: patient_relationship,
                    visibility: visibility.join()
                }
            });
            return res.data;
        });
    }
    /**
     * @param {Object} params
     * @param {Object} params.provider
     * @param {Array} params.location_ids
     * @param {String|Moment} params.period_start anything parseable by MomentJs
     * @param {String} params.purpose
     * @param {String} params.patient_relationship
     * @param {Array} params.visibility
     * @param {String} params.role
     * @returns {{schedRes: Array, lastApptQueriedFor: string}|{}} An object of a list of appointments and the last appointment queried for
     *
     * Returns a normalized list of appointments with additional properties
     * on each appointment @see decorateAppointments
     *
     */
    getScheduling({ provider, location_ids, period_start, purpose, patient_relationship, role, pull_waitlist_eligibility = false }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { customerCode } = this;
            const range = Api.buildRangeFromDate(period_start, Time_1.SCHED_RANGE_DAYS);
            const res = yield axios_1.default.get(this.apiBaseURL +
                `/api/scheduling/${encodeURIComponent(customerCode)}/availability/list`, {
                params: {
                    location_ids,
                    period_start: range.period_start,
                    period_end: range.period_end,
                    provider_id: provider.id,
                    purpose,
                    patient_relationship,
                    role,
                    pull_waitlist_eligibility
                }
            });
            const resData = res.data;
            return resData.result.length
                ? {
                    schedRes: resData.result.map(Api.decorateAppointment),
                    lastApptQueriedFor: range.period_end
                }
                : /** when there are no appointments returned we want the xstate service ev.data to be empty  */
                    {};
        });
    }
    /**
     * Based on params, fetches the next availability appointment from `period_start`
     * and then fetches a list of scheduling appointments starting from that point.
     *
     * NOTE:
     *  `/availability/next` and `/scheduling/list` have inconsistently timestamped slots due to data ingest issues with timezones.
     *  `/availability/next` may indicate that the next availability is a slot that is timestamped later than `/scheduling/list` has it,
     *  and when that timestamp is passed to scheduling slots may be cut off from the returned data.
     *  To adjsut for that, on initial load the date returned from `/availability/next` is set back to midnight and used as the start date for `/scheduling/list`.
     *  This is a stop-gap solution until the data is cleaned up.
     *
     * There have been cases where availability.next returned null but there were appointments
     * But, we still need availability.next to account for cases where the next available appointment is 2 months from now
     * So we only use availability.next if we don't have a start date
     * If we don't get results from availability.next we'll still call scheduling.list
     *
     * Returns a normalized list of appointments with additional properties
     * on each appointment @see decorateAppointments
     *
     * @param {object} functionParams Params
     * @param {Object} functionParams.params
     * @param {Object} functionParams.params.provider
     * @param {Array} functionParams.params.location_ids
     * @param {String|Moment} functionParams.params.period_start anything parseable by MomentJs
     * @param {String} functionParams.params.purpose
     * @param {String} functionParams.params.patient_relationship
     * @param {Array} functionParams.params.visibility
     * @param {String} functionParams.params.role
     * @param {Number} schedRangeDays number or appointment days to request
     * @returns {{schedRes: Array, lastApptQueriedFor: string}|{}} An object of a list of appointments and the last appointment queried for
     */
    getNextSchedulingList({ params, isInitialLoad }) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // get providers' next availablility
            const nextRes = yield this.getNext(params);
            // use availability.next returned start_datetime if exists, otherwise fall back to the provided params.period_start
            const periodStartMoment = ((_a = nextRes === null || nextRes === void 0 ? void 0 : nextRes.result) === null || _a === void 0 ? void 0 : _a.start_datetime)
                ? (0, moment_1.default)(nextRes.result.start_datetime)
                : (0, moment_1.default)(params.period_start);
            const { schedRes, lastApptQueriedFor } = yield this.getScheduling(Object.assign(Object.assign({}, params), { 
                // if initially loading scheduling data, set date back to midnight (see NOTE in function doc comment above ☝️)
                period_start: isInitialLoad
                    ? (0, utils_1.momentAtMidnight)(periodStartMoment).toDate()
                    : periodStartMoment.toDate() }));
            /** when there are no appointments returned we want the xstate service ev.data to be empty  */
            return schedRes ? { schedRes, lastApptQueriedFor } : {};
        });
    }
    getXpPlanNames() {
        return __awaiter(this, void 0, void 0, function* () {
            const planNamesResponse = yield axios_1.default.get(this.apiBaseURL + '/api/form/xp_plan_names');
            const _xp_plan_names = planNamesResponse.data || [];
            return _xp_plan_names;
        });
    }
    /**
     * @param {Function} getProviderByIdCallback  Callback that is passed into db-widget to communicate with search service for fetching a provider
     * @param {String} id  provider id
     * @param {Object} epicOSWConfig the epic OSW config object from CS (third_party_scheduling_configs.epic_osw)
     * @returns {Object} response
     * @returns {Object} response.provider The provider object from response, with _xp_plan_names from /api/_xp_plan_names appended
     */
    constructProviderObject(getProviderByIdCallback, id, epicOSWConfig, visibility) {
        return __awaiter(this, void 0, void 0, function* () {
            const providerResponse = yield getProviderByIdCallback(id);
            const { data: providerData = { provider: {}, locations: {} } } = providerResponse.data;
            providerData.provider.locations = providerData.locations;
            // if we're in the OSW flow, nothing else to do here, just return the provider data
            if ((0, epicOSWUtils_1.isOSWEnabled)(providerData.provider, epicOSWConfig)) {
                return providerData;
            }
            // fetch the appointment purposes
            const [establishedPurposesResponse, newPurposesResponse] = yield Promise.all([
                this.getAvailableApptPurposesForProvider(id, Filters_1.ESTABLISHED_PATIENT, visibility.role),
                this.getAvailableApptPurposesForProvider(id, Filters_1.NEW_PATIENT, visibility.role)
            ]);
            providerData.provider.availableApptPurposes = (0, utils_1.buildPurposesSetFromResponses)({
                establishedPurposesResponse,
                newPurposesResponse
            });
            return providerData;
        });
    }
    /**
     * Function that fetches the alternate provider data from SS
     *
     * @param {Function} getAltProvidersCallback Callback that is passed into db-widget to communicate with search service for fetching alt providers
     * @returns the provider response from SS
     */
    getAltProvidersData(getAltProvidersCallback, provider, locations, queryParams = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield getAltProvidersCallback(provider, locations, queryParams);
            // this should be the `_result` array from the search service
            return response;
        });
    }
    /**
     * @param {Object} props
     * @param {Object} appointment the selected appointment to be booked
     * @param {Object} patient patient info
     * @param {String} channel channel attached to this booking e.g: 'pmc', 'pmac', etc..
     *
     * @returns {Object} response from booking call
     */
    bookAppointment({ selectedAppointment, patientRegistration, ehrBoundGXData, channel = 'pmc', appointmentEhrId }) {
        return __awaiter(this, void 0, void 0, function* () {
            // NOTE: we only allow ONE phonenumber and ONE insurance within the form right now,
            // but the api expects these to be lists
            const { appointment, patient } = patientRegistration;
            const { phone: { number: phoneNumber, type: phoneType, consent: phone_consent }, name: patientNames } = patient, patientProps = __rest(patient, ["phone", "name"]);
            const _a = appointment || {}, { insurance, notes = '' } = _a, appointmentProps = __rest(_a, ["insurance", "notes"]);
            const insurances = insurance ? [insurance] : [];
            const phones = [{ number: (0, utils_1.formatApiPhone)(phoneNumber), type: phoneType }];
            const patientName = (0, utils_1.trimStringProperties)(patientNames);
            // Convert true to 'Y' to minimize request size due to some EHR limitations
            const phoneConsent = phone_consent === true && { phone_consent: CONSENT };
            const { telehealth_attestation } = ehrBoundGXData;
            /**These params are meant to be used with `scheduling_v2`,
             * @see https://stg-scheduling.kyruus.com/docs/#!/%3Ccustomer_code%3E/create_appointment_v2
             *
             * Adding new params:
             *     - Scheduling can receive arbitrary fields/params.
             *     - As long as the customer has the fields configured in `packed_appointment_fields`,
             *       Scheduling will add them to appointment notes in EHR.
             *     - @see https://kyruus.jira.com/wiki/spaces/AHC/pages/331677967/How+To+Sending+Registration+Data+in+Appointment+Notes
             */
            const body = Object.assign(Object.assign(Object.assign(Object.assign({}, appointmentProps), { channel: channel, 
                /**
                 * booking call happens only on client, so it is safe to access window here
                 * sending this field to scheduling so they can construct cancellation links back to booking origin
                 * this field is NOT forwarded to EHR systems
                 */
                direct_book_base_url: window.location.origin, slot: selectedAppointment, patient: Object.assign(Object.assign(Object.assign({}, patientProps), phoneConsent), { name: patientName, phones,
                    insurances, location_id: selectedAppointment.location_id, provider_id: selectedAppointment.provider_id }), provider_id: selectedAppointment.provider_id, insurances: insurances, source_system: selectedAppointment.source_system, start_datetime: selectedAppointment.start_datetime, duration_minutes: selectedAppointment.duration_minutes, patient_relationship: selectedAppointment.purpose.patient_relationship, purpose_name: selectedAppointment.purpose.name, availability_ids: [selectedAppointment.availability_id], type: selectedAppointment.type, reason: notes }), (telehealth_attestation && { telehealth_attestation })), (appointmentEhrId && { appointment_ehr_id: appointmentEhrId }));
            const res = yield axios_1.default.post(this.apiBaseURL + '/api/book_appointment', body);
            return res.data.data;
        });
    }
    /**
     * Function to call the getEhrPatientDemographic callback and separate the response data
     * into the demographic data and optionally the `patientList` if the `with_related_persons`
     * flag is set to true.
     * @param {String} pid a patient identifier for an EHR integration
     * @param {String} pid_type a patient identifier type for an EHR integration
     *
     * @returns {Object} Object containing the data from the API response
     */
    getEhrPatientDemographics(getEhrPatientDemographicsCallback, ehr) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const responseData = yield getEhrPatientDemographicsCallback(ehr);
                return responseData;
            }
            catch (error) {
                // if this case needs to be logged, it should be logged in the app that implements DBW
                return null;
            }
        });
    }
    /**
     * Function to call the getEhrPatientProxyDemographic callback
     * @param {String} pid a patient identifier for an EHR integration
     * @param {String} pid_type a patient identifier type for an EHR integration
     *
     * @returns {Object} Object containing the data from the API response
     */
    getEhrPatientProxyDemographics(getEhrPatientProxyDemographicsCallback, ehr) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const responseData = yield getEhrPatientProxyDemographicsCallback(ehr);
                return responseData;
            }
            catch (error) {
                // if this case needs to be logged, it should be logged in the app that implements DBW
                return [];
            }
        });
    }
    /**
     * Guided Experience V1
     * @returns {Array<Object>} response array of GxFlows from customer config
     */
    getGxFlowForProviderType(providerType) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const res = yield axios_1.default.get(this.apiBaseURL +
                    `/config/gx_flow/by_provider_type/${encodeURIComponent(providerType)}/`);
                return res.data.data;
            }
            catch (e) {
                // if we got 404 from the service, then that is a valid path and there is no gx flow
                return null;
            }
        });
    }
    /**
     * Guided Experience V2 - POST request to receive first GX question
     * @param {Number} provider_id
     */
    getGxStepByProviderId(providerId, body) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const res = yield axios_1.default.post(this.apiBaseURL + `/api/gx/provider/${providerId}/node`, body, {
                    headers: {
                        'x-consumer-username': 'pmc'
                    }
                });
                const results = res.data;
                return results;
            }
            catch (e) {
                // NOTE: if we don't find a gx flow or gxv2 is NOT enabled
                // for this provider a 404 will be returned
                return null;
            }
        });
    }
    getAvailableApptPurposesForProvider(providerId, patientRelationship, role) {
        return __awaiter(this, void 0, void 0, function* () {
            if (![Filters_1.ESTABLISHED_PATIENT, Filters_1.NEW_PATIENT].includes(patientRelationship)) {
                throw new Error('Invalid patient relationship supplied to getAvailableApptPurposesForProvider');
            }
            const visibilityKey = `visibilities.${role}`;
            const res = yield this.searchClient.getSlots({
                facet: [[Facets_1.PURPOSE]],
                filter: {
                    and: {
                        and: {
                            [visibilityKey]: ['direct_book'],
                            provider_id: [providerId],
                            patient_relationship: [patientRelationship]
                        }
                    }
                }
            });
            return res.data;
        });
    }
    /**
     * @param {Number} id Id of provider to fetch purposes from
     * @param {Object} apptInfo
     * @param {string} apptInfo.patientRel
     * @param {string} apptInfo.purpose
     *
     * @returns {Object} api response
     */
    getAvailableLocsForProvider(id, apptInfo, role) {
        return __awaiter(this, void 0, void 0, function* () {
            const visibilityKey = `visibilities.${role}`;
            const res = yield this.searchClient.getSlots({
                facet: [[Facets_1.LOCATION_ID]],
                filter: {
                    and: {
                        and: {
                            [visibilityKey]: ['direct_book'],
                            provider_id: [id],
                            patient_relationship: [apptInfo.patientRel],
                            purpose: [apptInfo.purpose]
                        }
                    }
                }
            });
            return res.data;
        });
    }
    checkCaptcha() {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield axios_1.default.get(this.apiBaseURL + '/captcha/check');
            return res.data.data;
        });
    }
    /**
     * @param {String} captchaToken captcha verification token
     *
     * @returns {Promise} which resolves to the `/verify` response
     */
    postCaptchaResponseToken(captchaToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield axios_1.default.post(this.apiBaseURL + '/captcha/verify', {
                response: captchaToken
            });
            return res.data;
        });
    }
}
exports.Api = Api;
//# sourceMappingURL=api.js.map